import { ShieldAlert, Lock, FileText } from "lucide-react";

const Wrap = ({ icon: Icon, title, children, testId }) => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={testId}>
    <div className="flex items-center gap-3 mb-6">
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-100 text-teal-700">
        <Icon className="w-6 h-6" />
      </span>
      <h1 className="font-heading text-3xl font-extrabold text-slate-900">{title}</h1>
    </div>
    <div className="prose-none space-y-5 text-lg text-slate-700 leading-relaxed">
      {children}
    </div>
  </div>
);

const H = ({ children }) => (
  <h2 className="font-heading font-bold text-xl text-slate-900 pt-2">{children}</h2>
);

export function DisclaimerPage() {
  return (
    <Wrap icon={ShieldAlert} title="Medical Disclaimer" testId="disclaimer-page">
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 text-amber-950 font-medium">
        AskMyDoctor AI provides general medical information and education only. It
        does not diagnose medical conditions and does not replace care from a
        physician or other qualified healthcare professional.
      </div>
      <H>Not a substitute for professional care</H>
      <p>
        The information on this website is for general educational purposes. It is
        not medical advice, and it is not a diagnosis, treatment plan, or
        prescription. Always talk with a licensed doctor, pharmacist, or other
        qualified healthcare professional about your personal health, your
        medicines, and any test results.
      </p>
      <H>No doctor–patient relationship</H>
      <p>
        Using this website does not create a doctor–patient relationship. We do not
        examine you, review your full medical history, or provide personalized
        medical care.
      </p>
      <H>Emergencies</H>
      <p>
        If you think you may be having a medical emergency, call 911 (or your local
        emergency number) or go to the nearest emergency room right away. Do not
        rely on this website in an emergency.
      </p>
      <H>Sources</H>
      <p>
        Our answers are drawn from free, public U.S. government medical sources,
        including MedlinePlus (National Library of Medicine), the U.S. Food and
        Drug Administration (openFDA), and RxNorm. These sources are reliable but
        general, and may not reflect your individual situation. We do our best to
        summarize them accurately, but we do not guarantee that every answer is
        complete or error-free.
      </p>
      <H>Medicines</H>
      <p>
        Never start, stop, or change a prescription medicine based on this website
        without talking to the doctor who prescribed it, unless the situation is an
        emergency. We never provide personalized dosing.
      </p>
    </Wrap>
  );
}

export function PrivacyPage() {
  return (
    <Wrap icon={Lock} title="Privacy Policy" testId="privacy-page">
      <p>
        Your privacy matters. AskMyDoctor AI is designed so you can ask a general
        medical question anonymously.
      </p>
      <H>We do not ask for personal information</H>
      <p>
        You do not need to give us your name, date of birth, email, phone number,
        address, or medical-record number to use this service. Please do not enter
        your name, address, Social Security number, or other personally identifying
        information into the question box.
      </p>
      <H>What we store</H>
      <p>
        To improve the service and understand which topics people need most, we may
        store the text of questions and the category they fall into, along with
        simple “helpful / not helpful” feedback. We do not link this to your
        identity. Please avoid including personal details in your questions.
      </p>
      <H>What we do not do</H>
      <p>
        We do not sell your questions. Our medical answers are independent and are
        never influenced by advertisers.
      </p>
      <H>Analytics</H>
      <p>
        If we use analytics in the future, we will avoid storing complete medical
        questions together with identifying information.
      </p>
    </Wrap>
  );
}

export function TermsPage() {
  return (
    <Wrap icon={FileText} title="Terms of Use" testId="terms-page">
      <p>
        By using AskMyDoctor AI, you agree to these terms. Please read them
        together with our Medical Disclaimer and Privacy Policy.
      </p>
      <H>Educational use only</H>
      <p>
        This website provides general medical information and education. It does
        not diagnose medical conditions and does not replace care from a physician
        or other qualified healthcare professional. You are responsible for
        decisions you make about your health, and you should confirm any
        information with a qualified professional.
      </p>
      <H>No warranty</H>
      <p>
        The service is provided “as is.” While we base answers on trusted public
        medical sources, we do not guarantee that information is complete, current,
        or suited to your situation.
      </p>
      <H>Emergencies</H>
      <p>
        Do not use this website for medical emergencies. Call 911 or seek emergency
        care immediately if you may be experiencing an emergency.
      </p>
      <H>Acceptable use</H>
      <p>
        Please do not submit personal identifying information, and do not use this
        service to attempt to diagnose or treat serious conditions on your own
        without professional guidance.
      </p>
      <H>Changes</H>
      <p>
        We may update these terms and the features of the service over time.
        Continued use means you accept the current terms.
      </p>
    </Wrap>
  );
}
